import dragSpineStyles from "./card-drag-spine.module.css"
import styles from "./card.module.css"

import { cx } from "@shared/components/untitledui/util/cx"
import { CardButtons } from "@shared/unified-tiptap/editor-ui/card-buttons/CardButtons"
import { CardPublicNotice } from "@shared/unified-tiptap/editor-ui/card-public-notice/CardPublicNotice"
import CardComments from "@shared/unified-tiptap/editor-ui/comments/CardComments"
import { DraggableNodeViewWrapper } from "@shared/unified-tiptap/editor-ui/drag-handle/DraggableNodeViewWrapper"
import { PLACEHOLDERS, placeholderStyle } from "@shared/unified-tiptap/placeholders"
import {
  CARD_BOTTOM_SEPARATOR_CLASS,
  CARD_COMMENTS_BOX_CLASS,
  CARD_WRAPPER_CLASS,
  cardWrapperData,
  getCardId,
  replaceCardPreservingIdentity,
  useCardComments,
  useIsLastCard,
} from "@shared/unified-tiptap/util/card"
import { tracked } from "@supergrain/kernel/react"
import type { Node as PMNode } from "@tiptap/pm/model"
import { NodeViewContent, type ReactNodeViewProps } from "@tiptap/react"
import { OPERATION_CONTRACTS } from "../../analytics/operations"
import { useHoverChrome } from "../../use-hover-chrome"
import { findAddTargetPos } from "../../util/find-add-target-pos"
import { type CardExtensionAttrs } from "./card-extensions"

// Full-bleed card box: the negative horizontal margin cancels the lesson body's p-3
// padding so the card spans the whole lesson width; px-3 restores the card's own content
// inset. The 8px of white above the title is the
// wrapper's pt-2 (NOT a top margin, which would collapse out of the useHoverChrome region
// — see the wrapper below), so the buttons reveal anywhere in the card's white.
const CARD_BOX = "-mx-3 px-3 pt-0"

// In its own file (with only component exports) so that edits hot-reload
// via React Fast Refresh instead of reloading the page.

// tracked(): useHoverChrome reads this surface's verdict as a signal.

/**
 * ReactNodeView component for cardExtensionFactory.
 */
export const CardExtensionComponent = tracked((props: ReactNodeViewProps) => {
  const attrs = props.node.attrs as CardExtensionAttrs

  const isLastCard = useIsLastCard(props.editor, attrs.id)
  const isTitled = props.node.type.name === "titledCard"
  const isUntitled = props.node.type.name === "untitledCard"

  // The card is the reveal region for its own chrome — the fixed buttons:
  // hover/focus is tracked here and the decision painted via data-chrome-visible
  // (see card.module.css).
  const { chromeVisible, chromeProps } = useHoverChrome("cardButtons")

  const comments = useCardComments(attrs)
  const hasComments = comments.length > 0

  /**
   * Where the Add menu inserts, as a findAddTargetPos-style position:
   * - Selection inside this card: the block containing it, so new content lands right
   *   after that block (or at the card's bottom when the selection is in the title).
   * - Selection elsewhere: the card's title, so new content lands at its bottom.
   */
  const getAddTargetPos = (): number | null => {
    const cardPos = props.getPos()
    if (cardPos === undefined) return null

    const { head } = props.editor.state.selection
    if (head > cardPos && head < cardPos + props.node.nodeSize) {
      return findAddTargetPos(props.editor, head).pos
    }
    // The card itself, represented by its first inner position (per findAddTargetPos docs).
    return cardPos + 1
  }

  /**
   * Paste the given card (of the same type) over this one, replacing our content
   * and appropriate attributes.
   */
  const pasteCard = (copiedCard: PMNode) => {
    const cardPos = props.getPos()
    if (cardPos === undefined) return

    const pastedCard = replaceCardPreservingIdentity(props.node, copiedCard)

    const sourceCardId = getCardId(copiedCard)
    const destinationCardId = getCardId(props.node)
    const chain = props.editor.chain()
    if (sourceCardId && destinationCardId) {
      chain.label(OPERATION_CONTRACTS.OP_PASTE_CARD, "card-menu", {
        sourceCardId,
        destinationCardId,
      })
    }
    chain
      .command(({ tr }) => {
        // Atomic replace, in case the schema needs it.
        tr.replaceWith(cardPos, cardPos + props.node.nodeSize, pastedCard)
        return true
      })
      .run()
  }

  return (
    <DraggableNodeViewWrapper
      {...props}
      wrapperClassName={cx(
        CARD_WRAPPER_CLASS,
        // Untitled cards use --note-pad to center note text; fixed padding keeps handle/buttons aligned.
        isUntitled && "[--note-pad:0.75rem]",
        props.editor.isEditable && dragSpineStyles.cardDragSpineWrapper,
        styles.cardWrapper,
      )}
      handleClassName={dragSpineStyles.cardDragSpine}
      wrapperProps={{ ...chromeProps, ...cardWrapperData({ chromeVisible, isLastCard }) }}
    >
      {/* The buttons overlay the card's top right (absolutely positioned) and take no
          layout space. */}
      <CardButtons
        {...props}
        mode="titledCard"
        getAddTargetPos={getAddTargetPos}
        pasteCard={pasteCard}
        // Only a titledCard can be posted to Google Classroom.
        canPostToGoogleClassroom={isTitled}
        // The column's icons center on the lesson body's white edge: the wrapper's right
        // gutter (2.25rem, where the buttons hang) less the card's own right content
        // padding (0.75rem) less half the button width, so it hangs in the margin with
        // only half a circle over the text. The wrapper's pt-2 would put the plus on the
        // title's first line; the stacked pill sits 2px above that so it reads centered on
        // the title and first body line.
        className={cx(
          "right-[calc(2.25rem_-_0.75rem_-_(14px_+_0.75rem)/2)]",
          isUntitled ? "top-[calc(0.375rem_+_var(--note-pad))]" : "top-1.5",
          styles.cardButtons,
        )}
      />
      {/* unified-tiptap-card is a global hook other extensions select on. */}
      <NodeViewContent
        className={cx(CARD_BOX, isUntitled && "py-(--note-pad)", "unified-tiptap-card")}
        data-id={attrs.id}
        // The empty-card prompt paints on a bare <p> ProseMirror generates between card
        // titles — no React element of its own, so the prompt is named here and inherits.
        style={placeholderStyle(PLACEHOLDERS.emptyCard)}
      />
      {hasComments && (
        // Full-bleed like the card box so the comments line up with its text.

        // In addition to contentEditable={false}, use tabIndex={-1} to allow
        // non-ProseMirror-controlled selections inside the comments.
        // From https://discuss.prosemirror.net/t/allow-text-selection-in-leaf-nodeviews/3438/2
        <div className={CARD_COMMENTS_BOX_CLASS} contentEditable={false} tabIndex={-1}>
          <CardComments comments={comments} />
        </div>
      )}
      {attrs.isPublic && <CardPublicNotice />}
      <div className={CARD_BOTTOM_SEPARATOR_CLASS} contentEditable={false} />
    </DraggableNodeViewWrapper>
  )
})
